import { List } from "@mui/material";
import { ListProps } from "@mui/material";
import React from "react";

import { copyToClipboard, formatAsHTML, formatWithTemplate } from "./copyUtils";
import ReviewGroupSubheader from "./ReviewGroupSubheader";
import ReviewListItem from "./ReviewListItem";
import ReviewListToolbar from "./ReviewListToolbar";
import { useReviewEditing } from "./useReviewEditing";
import { useReviewSelection } from "./useReviewSelection";

import type {
  ReviewItem,
  ReviewListAction,
  ReviewPanelItem,
} from "./ReviewPanel";

interface ReviewListProps extends ListProps {
  data: ReviewPanelItem[];
  onUpdate: (action: ReviewListAction) => void;
}

const ReviewList: React.FC<ReviewListProps> = ({
  data,
  onUpdate,
  ...listProps
}) => {
  const {
    checkedMap,
    allItemsMeta,
    isChildChecked,
    parentState,
    handleChildToggle,
    handleGroupToggle,
    handleTopToggle,
    removeFromCheckedMap,
    handleDeleteSelected,
  } = useReviewSelection(data, onUpdate);

  const {
    editingKey,
    editValue,
    setEditValue,
    startEditing,
    saveEditing,
    cancelEditing,
  } = useReviewEditing(onUpdate);

  const benefitNameByKey: Record<string, string> = Object.fromEntries(
    data.map((item) => [item.benefitKey, item.name]),
  );

  const handleCopy =
    (format: "markdown" | "plaintext" | "word") => async () => {
      const items = Array.from(checkedMap.values());
      if (items.length === 0) return;

      const plain = formatWithTemplate(
        items,
        format === "word" ? "plaintext" : format,
        benefitNameByKey,
      );

      if (format === "word") {
        const html = formatAsHTML(items, "word", benefitNameByKey);
        await copyToClipboard(plain, html);
      } else {
        await copyToClipboard(plain);
      }
    };

  return (
    <>
      <ReviewListToolbar
        allItemsMeta={allItemsMeta}
        checkedCount={checkedMap.size}
        onTopToggle={handleTopToggle}
        onCopy={handleCopy}
        onDeleteSelected={handleDeleteSelected}
      />

      <List {...listProps}>
        {data.map((item, index) => {
          const { childMetas, parentChecked, parentIndeterminate } =
            parentState(item);

          return (
            <React.Fragment key={`group-${item.benefitKey || index}`}>
              <ReviewGroupSubheader
                name={item.name}
                benefitKey={item.benefitKey}
                parentChecked={parentChecked}
                parentIndeterminate={parentIndeterminate}
                onChange={handleGroupToggle(childMetas)}
              />
              {(item.reviewItems ?? []).map(
                (reviewItem: ReviewItem, childIndex: number) => {
                  const itemKey = `${item.name}-${item.benefitKey}-${reviewItem.identifier || childIndex}`;
                  const uniqueCellId = `${item.benefitKey}-${reviewItem.cellIdentifier}`;

                  return (
                    <ReviewListItem
                      key={itemKey}
                      reviewItem={reviewItem}
                      item={item}
                      itemKey={itemKey}
                      isEditing={editingKey === itemKey}
                      isChecked={isChildChecked(uniqueCellId)}
                      editValue={editValue}
                      onEditValueChange={setEditValue}
                      onStartEditing={startEditing}
                      onSaveEditing={saveEditing}
                      onCancelEditing={cancelEditing}
                      onToggle={handleChildToggle(
                        uniqueCellId,
                        item.benefitKey,
                        reviewItem.identifier,
                        reviewItem.comment,
                      )}
                      onUpdate={onUpdate}
                      onRemoveFromCheckedMap={removeFromCheckedMap}
                    />
                  );
                },
              )}
            </React.Fragment>
          );
        })}
      </List>
    </>
  );
};

export default ReviewList;
